import { supabase } from '@/integrations/supabase/client';
import { ScoreEntry } from '@/types/cricket';

export const saveBattingStats = async (matchId: string, batsmanStats: any[]) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Delete existing batting stats for this match and innings
    await supabase
      .from('batting_stats')
      .delete()
      .eq('match_id', matchId)
      .eq('user_id', user.id);

    // Insert new batting stats
    const statsToInsert = batsmanStats.map(stat => ({
      match_id: matchId,
      player_id: stat.playerId,
      runs: stat.runs,
      balls: stat.balls,
      fours: stat.fours,
      sixes: stat.sixes,
      is_out: stat.isOut,
      is_on_strike: stat.isOnStrike,
      user_id: user.id
    }));

    if (statsToInsert.length > 0) {
      const { error } = await supabase
        .from('batting_stats')
        .insert(statsToInsert);
      if (error) throw error;
    }
  } catch (error) {
    console.error('Error saving batting stats:', error);
    throw error;
  }
};

export const saveBowlingStats = async (matchId: string, bowlerStats: any[]) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Delete existing bowling stats for this match and innings
    await supabase
      .from('bowling_stats')
      .delete()
      .eq('match_id', matchId)
      .eq('user_id', user.id);

    // Insert new bowling stats
    const statsToInsert = bowlerStats.map(stat => ({
      match_id: matchId,
      player_id: stat.playerId,
      overs: stat.overs,
      maidens: stat.maidens,
      runs_given: stat.runsGiven,
      wickets: stat.wickets,
      dots: stat.dots,
      economy_rate: stat.economyRate,
      user_id: user.id
    }));

    if (statsToInsert.length > 0) {
      const { error } = await supabase
        .from('bowling_stats')
        .insert(statsToInsert);
      if (error) throw error;
    }
  } catch (error) {
    console.error('Error saving bowling stats:', error);
    throw error;
  }
};

export const loadBattingStats = async (matchId: string) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('batting_stats')
      .select('*')
      .eq('match_id', matchId)
      .eq('user_id', user.id);

    if (error) throw error;

    return data?.map(stat => ({
      playerId: stat.player_id,
      runs: stat.runs || 0,
      balls: stat.balls || 0,
      fours: stat.fours || 0,
      sixes: stat.sixes || 0,
      isOut: stat.is_out || false,
      isOnStrike: stat.is_on_strike || false
    })) || [];
  } catch (error) {
    console.error('Error loading batting stats:', error);
    return [];
  }
};

export const loadBowlingStats = async (matchId: string) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('bowling_stats')
      .select('*')
      .eq('match_id', matchId)
      .eq('user_id', user.id);

    if (error) throw error;

    return data?.map(stat => ({
      playerId: stat.player_id,
      overs: stat.overs || 0,
      maidens: stat.maidens || 0,
      runsGiven: stat.runs_given || 0,
      wickets: stat.wickets || 0,
      dots: stat.dots || 0,
      economyRate: stat.economy_rate || 0,
      average: 0,
      strikeRate: 0
    })) || [];
  } catch (error) {
    console.error('Error loading bowling stats:', error);
    return [];
  }
};

export const saveScoreHistory = async (matchId: string, scoreEntry: ScoreEntry) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Calculate runs breakdown
    const bonusBoundary = scoreEntry.fours === 1 ? 4 : scoreEntry.sixes === 1 ? 6 : 0;
    const regularRuns = scoreEntry.runs - bonusBoundary - (scoreEntry.overthrows || 0) - (scoreEntry.extras || 0);

    const { error } = await supabase
      .from('score_history')
      .insert({
        match_id: matchId,
        over_number: scoreEntry.overNumber,
        ball_number: scoreEntry.ballNumber,
        runs: regularRuns, // Regular runs without boundaries and extras
        ball_runs: scoreEntry.ballRuns,
        extras: scoreEntry.extras,
        extra_type: scoreEntry.extraType,
        ball_count: scoreEntry.ballCount,
        bowler_runs: scoreEntry.bowlerRuns,
        batsman_runs: scoreEntry.batsmanRuns,
        bowler_id: scoreEntry.bowlerId,
        striker_id: scoreEntry.strikerId,
        non_striker_id: scoreEntry.nonStrikerId,
        wicket_id: scoreEntry.wicket?.id || null,
        is_free_hit: scoreEntry.isFreeHit,
        description: scoreEntry.description,
        user_id: user.id,
        ball_direction: scoreEntry.ballDirection || null,
        innings: scoreEntry.innings || 1,
        batting_team_id: scoreEntry.battingTeamId,
        bonus_boundary: bonusBoundary, // New field for 4s and 6s
        overthrows: scoreEntry.overthrows || 0 // New field for overthrows
      });

    if (error) throw error;

    // Upsert match_state for live roles
    const { error: msError } = await supabase
      .from('match_state')
      .upsert({
        match_id: matchId,
        current_striker_id: scoreEntry.strikerId,
        current_non_striker_id: scoreEntry.nonStrikerId,
        current_bowler_id: scoreEntry.bowlerId,
        current_over: scoreEntry.overNumber,
        current_ball: scoreEntry.ballNumber,
        user_id: user.id,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'match_id' });

    if (msError) {
      console.error('Error upserting match_state:', msError);
      throw msError;
    }
    return true;
  } catch (error) {
    console.error('Error saving score history:', error);
    return false;
  }
};

export const loadScoreHistory = async (matchId: string): Promise<ScoreEntry[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('score_history')
      .select(`
        *,
        wickets!score_history_wicket_id_fkey (
          id,
          player_id,
          bowler_id,
          wicket_type,
          innings,
          over_number,
          ball_number,
          runs_scored
        ),
        ball_direction
      `)
      .eq('match_id', matchId)
      .eq('user_id', user.id)
      .order('created_at', { ascending: true });

    if (error) throw error;

    return data?.map(entry => ({
      id: entry.id,
      runs: (entry.runs || 0) + (entry.bonus_boundary || 0) + (entry.overthrows || 0) + (entry.extras || 0), // Total runs
      ballRuns: entry.ball_runs || 0,
      extras: entry.extras || 0,
      extraType: entry.extra_type || '',
      ballCount: entry.ball_count || 0,
      bowlerRuns: entry.bowler_runs || 0,
      batsmanRuns: entry.batsman_runs || 0,
      wicket: entry.wickets ? {
        id: entry.wickets.id,
        matchId: entry.match_id,
        playerId: entry.wickets.player_id,
        dismissedBatterId: entry.wickets.player_id,
        bowlerId: entry.wickets.bowler_id,
        wicketType: entry.wickets.wicket_type,
        innings: entry.wickets.innings,
        overNumber: entry.wickets.over_number,
        ballNumber: entry.wickets.ball_number,
        runsScored: entry.wickets.runs_scored,
      } : undefined,
      timestamp: new Date(entry.created_at || '').getTime(),
      description: entry.description || '',
      ballNumber: entry.ball_number || 0,
      overNumber: entry.over_number || 0,
      innings: entry.innings || 1,
      isFreeHit: entry.is_free_hit || false,
      bowlerId: entry.bowler_id,
      strikerId: entry.striker_id,
      nonStrikerId: entry.non_striker_id,
      ballDirection: entry.ball_direction || '',
      battingStats: [],
      bowlingStats: [],
      battingTeamId: entry.batting_team_id,
      fours: entry.bonus_boundary === 4 ? 1 : 0,
      sixes: entry.bonus_boundary === 6 ? 1 : 0,
      overthrows: entry.overthrows || 0
    })) || [];
  } catch (error) {
    console.error('Error loading score history:', error);
    return [];
  }
};

export const deleteScoreHistoryEntry = async (entryId: string) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('score_history')
      .delete()
      .eq('id', entryId)
      .eq('user_id', user.id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting score history entry:', error);
    return false;
  }
};

export const updateScoreHistoryEntry = async (entryId: string, updatedFields: Partial<ScoreEntry>) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Prepare the update object, mapping ScoreEntry fields to DB columns
    const updateObj: any = {};
    if (typeof updatedFields.runs === 'number') updateObj.runs = updatedFields.runs;
    
    // Convert fours and sixes back to bonus_boundary
    if (typeof updatedFields.fours === 'number' && updatedFields.fours > 0) {
      updateObj.bonus_boundary = 4;
    } else if (typeof updatedFields.sixes === 'number' && updatedFields.sixes > 0) {
      updateObj.bonus_boundary = 6;
    } else if (updatedFields.fours === 0 && updatedFields.sixes === 0) {
      updateObj.bonus_boundary = 0;
    }

    if (typeof updatedFields.extras === 'number') updateObj.extras = updatedFields.extras;
    if (typeof updatedFields.extraType === 'string') updateObj.extra_type = updatedFields.extraType;
    if (typeof updatedFields.overthrows === 'number') updateObj.overthrows = updatedFields.overthrows;
    if (typeof updatedFields.bowlerRuns === 'number') updateObj.bowler_runs = updatedFields.bowlerRuns;
    if (typeof updatedFields.batsmanRuns === 'number') updateObj.batsman_runs = updatedFields.batsmanRuns;
    if (typeof updatedFields.ballDirection === 'string') updateObj.ball_direction = updatedFields.ballDirection;
    if (typeof updatedFields.isFreeHit === 'boolean') updateObj.is_free_hit = updatedFields.isFreeHit;
    if (typeof updatedFields.description === 'string') updateObj.description = updatedFields.description;
    // Add more fields as needed

    const { error } = await supabase
      .from('score_history')
      .update(updateObj)
      .eq('id', entryId)
      .eq('user_id', user.id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating score history entry:', error);
    return false;
  }
};
